import { useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  AlertBanner,
  AlertVariants,
  Box,
  Button,
  ButtonVariants,
  Divider,
  FormControlSizes,
  IconName,
  Text,
  Tooltip,
} from '@talos/kyoko';
import { useCallback } from 'react';
import styled from 'styled-components';
import { selectIsFormValid } from '../../OMS/NewOrder/OrderSlice';
import { useOrderService } from '../../OMS/NewOrder/useOrderService';
import { useOpenPreTradePanel } from '../hooks/useOpenPreTradePanel';

/** Link-style: only text opacity changes on hover, no background or outline. */
const LinkStyleButton = styled(Button)`
  &&&:hover:not(:disabled) {
    background: transparent;
    box-shadow: none;
    border-color: transparent;
    opacity: 0.8;
  }

  &&&:focus,
  &&&:focus-visible {
    outline: none;
    box-shadow: none;
    background: transparent;
    border-color: transparent;
  }
`;

interface OrderRequestChangedBannerProps {
  /** Whether the banner is shown for the currently active pre-trade session. */
  isActiveSession: boolean;
  /** Called after new scenario is triggered (optional) */
  onNewScenarioTriggered?: () => void;
}

/**
 * Warning banner shown when order request has changed since the scenario was generated.
 * Includes a link to generate a new scenario with the current order.
 */
export function OrderRequestChangedBanner({ isActiveSession, onNewScenarioTriggered }: OrderRequestChangedBannerProps) {
  const orderService = useOrderService();
  const {
    openPreTradePanel,
    isPreTradeLoading,
    ConfirmationDialog: PreTradeConfirmationDialog,
  } = useOpenPreTradePanel(orderService);
  const isFormValid = useAppStateSelector(selectIsFormValid);

  const isDisabled = !isActiveSession || !isFormValid || isPreTradeLoading;
  const openNewScenarioTooltip = !isFormValid ? 'Order form is not currently valid.' : undefined;

  const handleOpenNewScenario = useCallback(async () => {
    if (isDisabled) {
      return;
    }
    await openPreTradePanel();
    onNewScenarioTriggered?.();
  }, [isDisabled, onNewScenarioTriggered, openPreTradePanel]);

  return (
    <>
      <Box data-testid="order-request-changed-banner">
        <AlertBanner variant={AlertVariants.Notice} showStripes={false} neutralColors>
          <Text size="fontSizeSmall">Order request has changed. Current scenario may not reflect latest inputs.</Text>
          {isActiveSession && (
            <>
              <Divider orientation="vertical" />
              <Tooltip tooltip={openNewScenarioTooltip}>
                <LinkStyleButton
                  ghost
                  variant={ButtonVariants.Primary}
                  size={FormControlSizes.Small}
                  endIcon={IconName.ExternalLink}
                  onClick={handleOpenNewScenario}
                  disabled={isDisabled}
                  data-testid="open-new-scenario-button"
                >
                  Open New Scenario
                </LinkStyleButton>
              </Tooltip>
            </>
          )}
        </AlertBanner>
      </Box>
      {isActiveSession && PreTradeConfirmationDialog}
    </>
  );
}
